import {
  Badge,
  Box,
  Divider,
  Heading,
  HStack,
  Skeleton,
  Text,
} from "@chakra-ui/react";
import { KeyboardEvent, ReactNode } from "react";
import { FragmentType, getFragmentData, gql } from "__generated__/gql";
import {
  FragmentType as ServerFragmentType,
  getFragmentData as getServerFragmentData,
  serverGql,
} from "__generated__/serverGql";
import { AccessPlanType } from "__generated__/gql/graphql";
import { assertPresence } from "utils/assertions";
import { getStripePriceDescription } from "utils/stripePrices";
import PlanCardQuotaList from "./PlanCardQuotaList";

const accessPlanFragment = gql(/* GraphQL */ `
  fragment BillingPlanCardAccessPlan on AccessPlan {
    id
    name
    planType
    stripeProductId
    ...QuotaLimitsAccessPlan
  }
`);

const brandFragment = gql(/* GraphQL */ `
  fragment BillingPlanCardBrand on Brand {
    id
    accessPlanId
  }
`);

const stripeProductFragment = serverGql(/* ServerGraphQL */ `
  fragment BillingPlanCardStripeProduct on StripeProduct {
    id
    defaultPrice {
      id
      ...StripePriceDescription
    }
  }
`);

export enum PlanCardDisabledStyle {
  MUTED = "muted",
  UNAVAILABLE = "unavailable",
}

function getPriceDescription({
  isContactSalesPlan,
  isFree,
  stripeProduct,
}: {
  isContactSalesPlan: boolean;
  isFree: boolean;
  stripeProduct: ServerFragmentType<typeof stripeProductFragment> | null;
}) {
  if (isFree) return "Free";

  if (isContactSalesPlan) return "Custom pricing";

  return getStripePriceDescription(
    getServerFragmentData(stripeProductFragment, stripeProduct)?.defaultPrice,
  );
}

type Props = {
  accessPlan: FragmentType<typeof accessPlanFragment>;
  action?: ReactNode;
  brand: FragmentType<typeof brandFragment>;
  children?: ReactNode;
  isCurrentShown?: boolean;
  isDisabled?: boolean;
  disabledStyle?: PlanCardDisabledStyle;
  isSelected?: boolean;
  isPriceLoading?: boolean;
  onSelect?: (accessPlanId: UUID) => void;
  stripeProduct: ServerFragmentType<typeof stripeProductFragment> | null;
};

export default function PlanCard({
  accessPlan: accessPlanFragmentRef,
  action,
  brand: brandFragmentRef,
  children,
  isCurrentShown = true,
  isDisabled,
  disabledStyle = PlanCardDisabledStyle.MUTED,
  isSelected,
  isPriceLoading,
  onSelect,
  stripeProduct: stripeProductFragmentRef,
}: Props) {
  const accessPlan = getFragmentData(accessPlanFragment, accessPlanFragmentRef);
  const brand = getFragmentData(brandFragment, brandFragmentRef);

  const isCurrent = accessPlan.id === brand.accessPlanId;
  const isContactSalesPlan =
    accessPlan.planType === AccessPlanType.CUSTOM &&
    !accessPlan.stripeProductId;
  const isFree = accessPlan.planType === AccessPlanType.FREE;
  const isSelectable = !!onSelect && !isDisabled;

  const priceDescription = getPriceDescription({
    isContactSalesPlan,
    isFree,
    stripeProduct: stripeProductFragmentRef,
  });

  const handleSelect = () => {
    assertPresence(onSelect, "Expected plan card select handler.");

    onSelect(accessPlan.id);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!isSelectable) return;
    if (event.key !== "Enter" && event.key !== " ") return;

    event.preventDefault();
    handleSelect();
  };

  return (
    <Box
      aria-selected={isSelected || (isCurrentShown && isCurrent)}
      aria-disabled={isDisabled || undefined}
      borderColor="gray.200"
      borderRadius="md"
      borderWidth={1}
      data-selectable={isSelectable ? true : undefined}
      data-disabled-style={isDisabled ? disabledStyle : undefined}
      display="flex"
      flexDirection="column"
      gap={4}
      minHeight="11rem"
      onClick={isSelectable ? handleSelect : undefined}
      onKeyDown={handleKeyDown}
      p={4}
      role={isSelectable ? "button" : undefined}
      tabIndex={isSelectable ? 0 : undefined}
      transitionDuration="normal"
      transitionProperty="background, border-color, box-shadow, opacity"
      transitionTimingFunction="ease-in-out"
      sx={{
        "&[data-selectable='true']:hover": {
          borderColor: "magenta.dark",
        },
        [`&[data-disabled-style='${PlanCardDisabledStyle.UNAVAILABLE}']`]: {
          bgGradient: "linear(to-br, white, lightPinkButton.50)",
          borderColor: "lightPinkButton.200",
          borderStyle: "dashed",
          opacity: 0.88,
        },
        "[data-all-plan-cards-disabled='true'] &[aria-disabled='true']:not([aria-selected='true'])":
          {
            bg: "gray.50",
            bgGradient: "none",
            borderColor: "gray.200",
            borderStyle: "solid",
            opacity: 0.62,
          },
      }}
      _disabled={{
        bg: "gray.50",
        borderColor: "gray.200",
        opacity: 0.62,
      }}
      _selected={{
        borderColor: "magenta.dark",
        bgGradient: "linear(to-br, lightPinkButton.50, white)",
        boxShadow: "md",
      }}
    >
      <Box flex={1}>
        <HStack alignItems="flex-start" justifyContent="space-between" gap={3}>
          <Heading as="h3" color="magenta.veryDark" size="sm">
            {accessPlan.name}
          </Heading>

          {isCurrentShown && isCurrent && (
            <Badge colorScheme="lightPinkButton" variant="solid">
              Current
            </Badge>
          )}

          {(!isCurrentShown || !isCurrent) && isSelected && (
            <Badge colorScheme="lightPinkButton" variant="solid">
              Selected
            </Badge>
          )}
        </HStack>

        <Skeleton isLoaded={!isPriceLoading} mt={3}>
          <Text color="gray.700" fontSize="lg" fontWeight="semibold">
            {priceDescription ?? "Price unavailable"}
          </Text>
        </Skeleton>

        <Divider my={2} />

        <PlanCardQuotaList accessPlan={accessPlan} />

        {children}
      </Box>

      {action}
    </Box>
  );
}
